import { useNavigate } from 'react-router-dom';
import { Content, TabButton } from './styles';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

/**
 * Represents a navigation link item in the menu bar
 */
interface LinkItem {
  /** The path segment for navigation (e.g., '/profile') */
  path: string;
  /** Name of the Lucide React icon to display */
  icon: IconName;
  /** Text label displayed next to the icon */
  label: string;
  /**  */
  action?: () => void;
}

interface MenuBarProps {
  /** Array of link items to display in the menu */
  links: LinkItem[];
  /** Current active path to determine which tab is active */
  currentPath: string;
}

/**
 * Horizontal navigation menu bar with responsive behavior
 *
 * @remarks
 * This component displays a series of tab buttons with icons and labels.
 * On small screens, only icons are shown to save space.
 *
 * @example
 * ```tsx
 * const links = [
 *   { path: '/profile', icon: 'user', label: 'Profile' },
 *   { path: '/security', icon: 'lock', label: 'Security' }
 * ];
 *
 * <MenuBar links={links} currentPath="profile" />
 * ```
 */
function MenuBar({ links, currentPath }: MenuBarProps) {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();

  return (
    <Content sx={{ width: '100%', overflowX: 'auto' }}>
      {links.map((link, index) => {
        const isActive = currentPath === link.path.replace('/', '');

        return (
          <>
            {/* Divider between tabs, except before first item */}
            <TabButton
              key={index}
              /** Determines if this tab is currently active */
              active={isActive}
              /** Keeps the label expanded for the active tab on larger screens */
              className={!isMd && isActive ? 'label-visible' : undefined}
              onClick={
                link.action
                  ? link.action
                  : () => navigate('/settings' + link.path)
              }
              /** Accessibility: provide context for screen readers */
              aria-current={isActive ? 'page' : undefined}
            >
              {/* Dynamic icon component */}
              <DynamicIcon name={link.icon} size="18px" />
              {/* Label grows in from the left on hover, or stays visible while active */}
              <span className="tab-label">{link.label}</span>
            </TabButton>
            <div className="divider" />
          </>
        );
      })}
    </Content>
  );
}

export default MenuBar;
